/**
 * Browser file-download helpers.
 *
 * The anchor-element dance was previously duplicated inline in AdminSessions
 * (CSV export) and ics.ts (calendar files). It lives here once so a fix — e.g.
 * the missing `revokeObjectURL` that leaks a blob per download — applies
 * everywhere.
 */

/** Trigger a browser download for an in-memory blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Without this the blob stays in memory for the life of the document — which
  // matters when an export is several megabytes.
  URL.revokeObjectURL(url)
}

/**
 * Read the server-chosen filename out of a `Content-Disposition` header.
 *
 * The backend builds a filter-aware name (`sessions_cancelled_2026-07-01_….xlsx`),
 * so preferring it over a client-side guess keeps the downloaded file
 * self-describing. Handles both the plain `filename="…"` and the RFC 5987
 * `filename*=UTF-8''…` forms, preferring the latter when present.
 */
export function filenameFromDisposition(
  disposition: string | undefined | null,
  fallback: string,
): string {
  if (!disposition) return fallback

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, '')) || fallback
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1]?.trim() || fallback
}

/** Extension the user should see for a given export format. */
export function extensionForFormat(format: string): string {
  return format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv'
}
